import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { formatWeekdayMonthDay } from '../reportDate';
import type {
  ReportFeatureCards,
  ReportFeedRow,
  ReportPostCard,
  ReportReviewCard,
  ReportServiceCard,
  ReportUpcoming,
  ReportWorkRow,
} from '../types/report.types';
import {
  BODY_FG,
  CARD_BORDER,
  FAINT_FG,
  FEED_RULE,
  FONT,
  HEADING_FG,
  INNER_RULE,
  MUTED_FG,
  PLACEHOLDER_BG,
  PLACEHOLDER_FG,
  POSITIVE_BG,
  POSITIVE_FG,
  RANK_OTHER_FG,
  REPLY_BG,
  STAR_FG,
  SURFACE,
  WORK_PHOTO_BG,
  WORK_PHOTO_FG,
  WORK_POST_BG,
  WORK_POST_FG,
  WORK_RULE_STRONG,
  WORK_SERVICE_BG,
  WORK_SERVICE_FG,
} from '../reportTokens';

/**
 * Covers every `ChangeRecord.changeType`, plus the two kinds the report adds
 * itself. The fallback below prints the raw key, so a type missing from here
 * shows a client the database's own word for it — `service_area`. Lives here
 * rather than in `MoreSections.tsx`: `Feed` (below) is its only reader now
 * that `WorkItems`/"What went live" — the old list this table was built for —
 * has been retired in favour of the design's own feed.
 */
const KIND_LABEL: Record<string, string> = {
  review_reply: 'Review reply',
  page: 'Page',
  post: 'Post',
  services: 'Service',
  update: 'Profile update',
  description: 'Description',
  categories: 'Categories',
  hours: 'Business hours',
  attributes: 'Attributes',
  photos: 'Photos',
  service_area: 'Service area',
  links: 'Links',
  labels: 'Labels',
  other: 'Profile update',
};

/** One feature card's category pill — the only header a card gets, since none
 * of the three card shapes the backend sends (`ReportPostCard`,
 * `ReportReviewCard`, `ReportServiceCard`) carries a date to put beside it. */
function CardTag({
  label,
  fg,
  bg,
  px = '8px',
}: {
  label: string;
  fg: string;
  bg: string;
  /** 9px on the What's-next pill, 8px everywhere else. The design's own
   * one-pixel difference, kept rather than averaged away. */
  px?: string;
}) {
  return (
    <Box
      component='span'
      sx={{
        fontSize: '10px',
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: fg,
        bgcolor: bg,
        padding: `3px ${px}`,
        borderRadius: '9999px',
        alignSelf: 'flex-start',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Box>
  );
}

/**
 * A feature card's header bar: the kind pill, and the day the work went live.
 *
 * Its own rule above the body is what makes the three cards read as one set
 * in the design — without it the pill floats over whatever each card happens
 * to start with (a photo, an avatar, a title) and the row loses its line.
 *
 * The date is optional because a v3 snapshot written before `cardDate` existed
 * has cards but no dates; the bar then holds the pill alone, as it did.
 */
function CardHeader({
  children,
  date,
}: {
  children: React.ReactNode;
  date?: string | null;
}) {
  const when = formatWeekdayMonthDay(date ?? null);
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        padding: '12px 14px',
        borderBottom: '1px solid',
        borderColor: INNER_RULE,
      }}
    >
      {children}
      {when && (
        <Typography component='span' sx={{ fontSize: '12px', color: FAINT_FG }}>
          {when}
        </Typography>
      )}
    </Box>
  );
}

/** Every feature card's body: the design's 18px box, flexed so the footer of
 * a tall card can sit on `margin-top: auto`. Only the gap differs per card. */
const cardBodySx = {
  padding: '18px',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
} as const;

/** A card's own footer rule: the design closes the post and service cards
 * with a line and a muted line of detail, pushed to the bottom so two cards
 * of different heights still end together. */
const cardFooterSx = {
  mt: 'auto',
  pt: '10px',
  borderTop: '1px solid',
  borderColor: SURFACE,
} as const;

/** The headline inside a feature card. */
const cardTitleSx = {
  fontSize: '16px',
  fontWeight: 700,
  lineHeight: 1.4,
  letterSpacing: '-0.01em',
} as const;

/** A feature card's outer frame — the border, radius and column layout every
 * one of the three shares, so only the body differs below. */
function CardFrame({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: INNER_RULE,
        borderRadius: '14px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
      }}
    >
      {children}
    </Box>
  );
}

/**
 * The GBP post card. The design also shows a caption body, a call-to-action
 * and a view count (`post.body`, `post.cta`, `post.views`) — none of which
 * `buildPostCard` sends (see `services/hostedReport/workCards.js`), so this
 * renders only the caption-derived title rather than inventing the rest.
 */
function PostCard({ post }: { post: ReportPostCard }) {
  return (
    <CardFrame>
      <CardHeader date={post.date}>
        <CardTag label='GBP post' fg={WORK_POST_FG} bg={WORK_POST_BG} />
      </CardHeader>
      {post.imageUrl ? (
        <Box
          component='img'
          src={post.imageUrl}
          alt={post.title}
          sx={{
            width: '100%',
            height: 220,
            objectFit: 'cover',
            display: 'block',
          }}
        />
      ) : (
        // No thumbnail was captured with this post — the design's own
        // placeholder illustration, not a broken <img>.
        <Box
          sx={{
            width: '100%',
            height: 220,
            background: PLACEHOLDER_BG,
            color: PLACEHOLDER_FG,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          <Box
            component='svg'
            viewBox='0 0 24 24'
            width={30}
            height={30}
            fill='none'
            stroke='currentColor'
            strokeWidth={1.5}
            aria-hidden
          >
            <rect x='3' y='4' width='18' height='16' rx='2' />
            <circle cx='8.5' cy='9.5' r='1.5' />
            <path d='M21 16l-5-5L5 20' />
          </Box>
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 600,
              letterSpacing: '0.04em',
            }}
          >
            Project photo
          </Typography>
        </Box>
      )}
      <Box sx={{ ...cardBodySx, gap: '10px' }}>
        <Typography sx={cardTitleSx}>{post.title}</Typography>
        {post.body && (
          <Typography
            sx={{ fontSize: '14px', color: BODY_FG, lineHeight: 1.65 }}
          >
            {post.body}
          </Typography>
        )}
        {post.cta && (
          // The design also prints a view count on the right of this row.
          // Google retired per-post insights, so there is no figure to print
          // and the button sits alone rather than beside a fabricated one.
          <Box
            sx={{
              ...cardFooterSx,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.25,
            }}
          >
            <Typography
              component='span'
              sx={{ fontSize: '13px', fontWeight: 600, color: WORK_POST_FG }}
            >
              {post.cta} →
            </Typography>
          </Box>
        )}
      </Box>
    </CardFrame>
  );
}

const StarRow = ({ stars }: { stars: number }) => (
  <Typography
    aria-label={`${stars} star${stars === 1 ? '' : 's'}`}
    sx={{ fontSize: '14px', color: STAR_FG, letterSpacing: '0.08em' }}
  >
    {'★'.repeat(stars)}
    {'☆'.repeat(Math.max(0, 5 - stars))}
  </Typography>
);

/** The review-and-our-reply card. `services/hostedReport/workCards.js`
 * refuses to build this card at all without reply text, so `review.reply` is
 * never empty here. */
function ReviewCard({ review }: { review: ReportReviewCard }) {
  return (
    <CardFrame>
      <CardHeader date={review.date}>
        <CardTag label='Review reply' fg={POSITIVE_FG} bg={POSITIVE_BG} />
      </CardHeader>
      <Box sx={{ ...cardBodySx, gap: '14px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box
            aria-hidden
            sx={{
              width: 38,
              height: 38,
              flex: '0 0 auto',
              borderRadius: '50%',
              bgcolor: SURFACE,
              color: BODY_FG,
              fontSize: '13px',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {review.initials}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
              {review.reviewerName}
            </Typography>
            {review.stars !== null && <StarRow stars={review.stars} />}
          </Box>
        </Box>
        <Typography
          sx={{ fontSize: FONT.body, color: BODY_FG, lineHeight: 1.65 }}
        >
          {review.reviewComment}
        </Typography>
        <Box
          sx={{
            mt: 'auto',
            bgcolor: REPLY_BG,
            borderLeft: `3px solid ${WORK_RULE_STRONG}`,
            borderRadius: '0 10px 10px 0',
            padding: '14px 16px',
          }}
        >
          <Typography
            sx={{
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: WORK_POST_FG,
              mb: '6px',
            }}
          >
            Replied to by us
          </Typography>
          <Typography
            sx={{ fontSize: FONT.body, color: HEADING_FG, lineHeight: 1.65 }}
          >
            {review.reply}
          </Typography>
        </Box>
      </Box>
    </CardFrame>
  );
}

/**
 * The listed-service card. The design also shows a category pill and a body
 * paragraph (`service.category`, `service.body`) — `buildServiceCard` sends
 * only the service's new name (see `services/hostedReport/workCards.js`), so
 * this renders the title alone rather than inventing a category or copy.
 */
function ServiceCard({ service }: { service: ReportServiceCard }) {
  return (
    <CardFrame>
      <CardHeader date={service.date}>
        <CardTag
          label='Listed service'
          fg={WORK_SERVICE_FG}
          bg={WORK_SERVICE_BG}
        />
      </CardHeader>
      <Box sx={{ ...cardBodySx, gap: '12px' }}>
        {service.category && (
          <Box
            component='span'
            sx={{
              fontSize: '11px',
              color: MUTED_FG,
              border: '1px solid',
              borderColor: CARD_BORDER,
              padding: '3px 9px',
              borderRadius: '9999px',
              alignSelf: 'flex-start',
            }}
          >
            {service.category}
          </Box>
        )}
        <Typography sx={cardTitleSx}>{service.title}</Typography>
        {service.body && (
          <Typography
            sx={{ fontSize: '14px', color: BODY_FG, lineHeight: 1.65 }}
          >
            {service.body}
          </Typography>
        )}
        {service.note && (
          <Typography
            sx={{ ...cardFooterSx, fontSize: '12px', color: FAINT_FG }}
          >
            {service.note}
          </Typography>
        )}
      </Box>
    </CardFrame>
  );
}

/** The three feature cards, in whatever subset actually exists this week. */
export function FeatureCards({ cards }: { cards?: ReportFeatureCards }) {
  if (!cards) return null;
  const present = [
    cards.post && <PostCard key='post' post={cards.post} />,
    cards.review && <ReviewCard key='review' review={cards.review} />,
    cards.service && <ServiceCard key='service' service={cards.service} />,
  ].filter(Boolean);
  if (present.length === 0) return null;
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(245px, 1fr))',
        gap: '18px',
        alignItems: 'stretch',
        mb: '20px',
      }}
    >
      {present}
    </Box>
  );
}

/** The five counts tiles — number then label, the way the design pairs them,
 * not the boxed label-and-count row an earlier draft of this section used. */
export function CountsRow({ rows }: { rows: ReportWorkRow[] }) {
  if (!rows.length) return null;
  // `buildWork` (services/hostedReportSnapshot.js) always emits a sixth row,
  // 'profileUpdates' — a catch-all so an hours/description/photo change is
  // never silently dropped from the count. The design has only five tiles,
  // so a normal week (no such change) should match it exactly; the catch-all
  // only earns its place on-page once it actually has something to report.
  // The stored row itself is unconditional — this is a rendering rule, not a
  // data rule, and every OTHER row still renders at zero, which is real
  // information ("no citation listings this week").
  const visible = rows.filter(
    row => row.key !== 'profileUpdates' || row.count > 0,
  );
  if (!visible.length) return null;
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '10px',
        borderTop: '1px solid',
        borderColor: INNER_RULE,
        pt: '16px',
      }}
    >
      {visible.map(row => (
        <Box
          key={row.key}
          sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}
        >
          <Typography
            component='span'
            sx={{
              fontSize: '20px',
              fontWeight: 700,
              letterSpacing: '-0.01em',
            }}
          >
            {row.count}
          </Typography>
          <Typography
            component='span'
            sx={{ fontSize: '12px', color: MUTED_FG, lineHeight: 1.4 }}
          >
            {row.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

/**
 * Colour and label for a feed row, by the real `kind` values `buildFeed`
 * passes through from `workItems` (`post`, `review_reply`, `services`, …) —
 * not the design mockup's own made-up categories ("Post", "Photo", "Q&A"),
 * which never appear in the real payload.
 */
function feedTagStyle(kind: string): { fg: string; bg: string } {
  if (kind === 'post') return { fg: WORK_POST_FG, bg: WORK_POST_BG };
  if (kind === 'review_reply') return { fg: POSITIVE_FG, bg: POSITIVE_BG };
  if (kind === 'services' || kind === 'service_area')
    return { fg: WORK_SERVICE_FG, bg: WORK_SERVICE_BG };
  if (kind === 'photos') return { fg: WORK_PHOTO_FG, bg: WORK_PHOTO_BG };
  return { fg: BODY_FG, bg: SURFACE };
}

const formatFeedDate = (iso: string | null) =>
  formatWeekdayMonthDay(iso) || '—';

/** "Everything published" — every work item, four shown and the rest behind
 * a toggle so a busy week does not push the rest of the report down the page. */
export function Feed({ rows }: { rows: ReportFeedRow[] }) {
  const [expanded, setExpanded] = useState(false);
  if (!rows.length) return null;
  const visible = expanded ? rows : rows.slice(0, 4);

  return (
    <Box sx={workBlockSx}>
      <Typography sx={{ ...cardTagHeadingSx, mb: '12px' }}>
        Everything published · {rows.length} item{rows.length === 1 ? '' : 's'}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {visible.map((row, index) => {
          const style = feedTagStyle(row.type);
          return (
            <Box
              key={`${row.type}-${row.date}-${index}`}
              sx={{
                // Three columns, not a flex row: the design lines every tag
                // up at one width so the titles start on a common edge. A
                // flex row sized each tag to its own word and left the
                // column ragged.
                display: 'grid',
                gridTemplateColumns: '118px minmax(0, 1fr) auto',
                gap: '14px',
                alignItems: 'center',
                padding: '11px 4px',
                borderBottom: '1px solid',
                borderColor: FEED_RULE,
              }}
            >
              <Box
                component='span'
                sx={{
                  fontSize: '10px',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: style.fg,
                  bgcolor: style.bg,
                  padding: '3px 8px',
                  borderRadius: '9999px',
                  justifySelf: 'start',
                }}
              >
                {KIND_LABEL[row.type] || row.type}
              </Box>
              <Typography
                sx={{
                  fontSize: '13px',
                  color: RANK_OTHER_FG,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {row.text}
              </Typography>
              <Typography
                sx={{
                  fontSize: '12px',
                  color: FAINT_FG,
                  whiteSpace: 'nowrap',
                }}
              >
                {formatFeedDate(row.date)}
              </Typography>
            </Box>
          );
        })}
      </Box>
      {rows.length > 4 && (
        <Box
          component='button'
          type='button'
          className='report-no-print'
          onClick={() => setExpanded(value => !value)}
          sx={{
            border: 0,
            bgcolor: 'transparent',
            p: 0,
            mt: '14px',
            fontSize: '13px',
            fontWeight: 600,
            color: WORK_RULE_STRONG,
            cursor: 'pointer',
          }}
        >
          {expanded ? '▴ Show less' : `▾ Show all ${rows.length}`}
        </Box>
      )}
    </Box>
  );
}

/** "Everything published", "What's next" — the small caps heading that opens
 * a block inside the Work card. */
const cardTagHeadingSx = {
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: BODY_FG,
} as const;

/** The rule-and-space a block below the feature cards opens with. */
const workBlockSx = {
  mt: '20px',
  borderTop: '1px solid',
  borderColor: INNER_RULE,
  pt: '18px',
} as const;

/** Up to three cards of work already booked beyond this week. */
export function UpcomingWork({ items }: { items: ReportUpcoming[] }) {
  if (!items.length) return null;
  return (
    <Box sx={workBlockSx}>
      <Typography sx={{ ...cardTagHeadingSx, mb: '4px' }}>
        What&apos;s next
      </Typography>
      <Typography sx={{ fontSize: '13px', color: MUTED_FG, mb: '14px' }}>
        Work scheduled for the coming weeks.
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(245px, 1fr))',
          gap: '14px',
        }}
      >
        {items.slice(0, 3).map((item, index) => (
          <Box
            key={`${item.when}-${index}`}
            sx={{
              border: '1px solid',
              borderColor: INNER_RULE,
              borderRadius: '14px',
              padding: '16px 18px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            <CardTag
              label={item.when}
              fg={WORK_POST_FG}
              bg={WORK_POST_BG}
              px='9px'
            />
            <Typography
              sx={{
                fontSize: '15px',
                fontWeight: 700,
                letterSpacing: '-0.01em',
                lineHeight: 1.35,
              }}
            >
              {item.title}
            </Typography>
            <Typography
              sx={{ fontSize: '13px', color: BODY_FG, lineHeight: 1.6 }}
            >
              {item.body}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
